import React from "react";

import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";

import { MenuItemBadgeDto } from "services/menuItemBadges/restModels";
import { CloseIcon, IconButton } from "shared/ui";
import { localizedText } from "shared/lib/localizedMap";

import { BadgePill } from "features/MenuItemBadges/components/BadgePill";
import { useMenuItemEditorContext } from "features/MenuItemEditor/context";

import {
    AddButton,
    AvailableRow,
    Empty,
    HiddenMark,
    SelectedChip,
    SelectedRow,
    Zone,
    ZoneLabel,
} from "./styles";

/*
* Выбранные бейджи стоят отдельным рядом и перетаскиваются: порядок чипов — это порядок
* badgeIds, то есть порядок плашек на карточке в меню. Ряд горизонтальный, потому что
* показывает ровно то, что увидит гость.
* */
export const BadgeChipsOrder = () => {
    const state = useMenuItemEditorContext();
    const { badgesCatalog, badgeIds, locale } = state;

    const selected = badgeIds
        .map((badgeId) => badgesCatalog.find((badge) => badge.id === badgeId))
        // Бейдж мог быть удалён из справочника в другой вкладке — неизвестный id пропускаем.
        .filter((badge): badge is MenuItemBadgeDto => !!badge);
    const available = badgesCatalog.filter((badge) => badgeIds.indexOf(badge.id) === -1);

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const moved = selected[result.source.index];
        const target = selected[result.destination.index];

        if (moved && target) state.moveBadge(moved.id, target.id);
    };

    if (badgesCatalog.length === 0) {
        return <Empty>Бейджей пока нет — их создают в разделе «Меню → Бейджи».</Empty>;
    }

    return (
        <Zone>
            <ZoneLabel>Выбранные — в порядке показа на карточке</ZoneLabel>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="menu-item-badges" direction="horizontal">
                    {(droppable) => (
                        <SelectedRow ref={droppable.innerRef} {...droppable.droppableProps}>
                            {selected.length === 0 && <Empty>Бейджи не выбраны</Empty>}
                            {selected.map((badge, index) => (
                                <Draggable
                                    key={badge.id}
                                    draggableId={String(badge.id)}
                                    index={index}
                                >
                                    {(draggable, snapshot) => (
                                        <SelectedChip
                                            ref={draggable.innerRef}
                                            {...draggable.draggableProps}
                                            {...draggable.dragHandleProps}
                                            $dragging={snapshot.isDragging}
                                        >
                                            <BadgePill
                                                title={localizedText(badge.title, locale)}
                                                fill={badge.fill}
                                                textColor={badge.textColor}
                                            />
                                            {/* Выключенный в справочнике бейдж остаётся у позиции,
                                                но в меню его не видно — иначе непонятно, почему. */}
                                            {!badge.isEnabled && <HiddenMark>скрыт</HiddenMark>}
                                            <IconButton
                                                label="Снять бейдж"
                                                size="small"
                                                onClick={() => state.removeBadge(badge.id)}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </SelectedChip>
                                    )}
                                </Draggable>
                            ))}
                            {droppable.placeholder}
                        </SelectedRow>
                    )}
                </Droppable>
            </DragDropContext>

            {available.length > 0 && (
                <>
                    <ZoneLabel>Доступные — нажмите, чтобы добавить</ZoneLabel>
                    <AvailableRow>
                        {available.map((badge) => (
                            <AddButton
                                key={badge.id}
                                type="button"
                                onClick={() => state.addBadge(badge.id)}
                            >
                                <BadgePill
                                    title={localizedText(badge.title, locale)}
                                    fill={badge.fill}
                                    textColor={badge.textColor}
                                />
                            </AddButton>
                        ))}
                    </AvailableRow>
                </>
            )}
        </Zone>
    );
};
